/**
 * Turns the text of table cells into real spreadsheet values (numbers, percentages, currency, dates)
 * so a converted table can be summed, sorted and charted. Pure and unit-tested.
 *
 * The rule throughout: convert only what is clearly a number or date, and decide by COLUMN, not cell
 * by cell. A column becomes numeric only when nearly all of its entries are numbers; IDs with
 * leading zeros, phone numbers, ZIP codes and long account numbers stay text. A slash date such as
 * 09/02/2026 could be day-first or month-first; the column's other dates settle it, and when nothing
 * does, the date is left as text (or follows the option the person chose) rather than guessed.
 */

export type DateOrder = "auto" | "dmy" | "mdy";

export type SheetCell =
  | { t: "s"; v: string }
  | { t: "n"; v: number; z: string }
  | { t: "d"; v: number; z: string };

interface ParsedNumber {
  value: number;
  kind: "plain" | "currency" | "percent";
  symbol?: string;
  /** The symbol was written after the amount (1.234,56 €). */
  symbolAfter?: boolean;
  decimals: number;
  thousands: boolean;
}

const CURRENCY = "$€£¥₹";
const NBSP = /[    ]/g;
const MINUS = /[−–‒]/g;

/** Reads one cell as a number, or null when it is not clearly one. */
export function parseNumber(text: string): ParsedNumber | null {
  let s = text.replace(NBSP, " ").replace(MINUS, "-").trim();
  if (!s || s.length > 30) return null;

  let negative = false;
  // (123.45) and ($123.45) are negative.
  const paren = /^\((.*)\)$/.exec(s);
  if (paren) {
    negative = true;
    s = paren[1].trim();
  }
  // Trailing minus: 123.45-
  if (/-$/.test(s) && !/^-/.test(s)) {
    negative = !negative;
    s = s.slice(0, -1).trim();
  }
  // Leading sign, before or after the currency symbol.
  let sign = "";
  const lead = /^([+-])\s*/.exec(s);
  if (lead) {
    sign = lead[1];
    s = s.slice(lead[0].length);
  }
  let symbol: string | undefined;
  let symbolAfter = false;
  if (s.length && CURRENCY.includes(s[0])) {
    symbol = s[0];
    s = s.slice(1).trim();
    const lead2 = /^([+-])\s*/.exec(s);
    if (lead2 && !sign) {
      sign = lead2[1];
      s = s.slice(lead2[0].length);
    }
  } else if (s.length && CURRENCY.includes(s[s.length - 1])) {
    symbol = s[s.length - 1];
    symbolAfter = true;
    s = s.slice(0, -1).trim();
  }
  let percent = false;
  if (s.endsWith("%")) {
    percent = true;
    s = s.slice(0, -1).trim();
  }
  if (sign === "-") negative = !negative;
  if (!s || !/^\d[\d.,]*$|^[.,]\d+$/.test(s)) return null;

  let intPart: string;
  let fracPart = "";
  let thousands = false;
  let m: RegExpExecArray | null;
  if ((m = /^(\d{1,3}(?:,\d{3})+)(?:\.(\d+))?$/.exec(s))) {
    // 1,234,567.89
    intPart = m[1].replace(/,/g, "");
    fracPart = m[2] ?? "";
    thousands = true;
  } else if ((m = /^(\d{1,3}(?:\.\d{3})+),(\d+)$/.exec(s))) {
    // 1.234.567,89 (European)
    intPart = m[1].replace(/\./g, "");
    fracPart = m[2];
    thousands = true;
  } else if ((m = /^(\d+)\.(\d+)$/.exec(s))) {
    intPart = m[1];
    fracPart = m[2];
  } else if ((m = /^(\d+),(\d{1,2})$/.exec(s))) {
    // 12,50 -> decimal comma (a thousands group always has three digits)
    intPart = m[1];
    fracPart = m[2];
  } else if ((m = /^(\d+)$/.exec(s))) {
    intPart = m[1];
  } else if ((m = /^\.(\d+)$/.exec(s))) {
    intPart = "0";
    fracPart = m[1];
  } else return null;

  // IDs with leading zeros ("007", "0123") are text; "0" and "0.5" are numbers.
  if (intPart.length > 1 && intPart.startsWith("0")) return null;
  if (intPart.length + fracPart.length > 15) return null;

  let value = Number(`${intPart}.${fracPart || "0"}`);
  if (!Number.isFinite(value)) return null;
  const decimals = fracPart.length;
  if (percent) value = Number((value / 100).toFixed(decimals + 2));
  if (negative) value = -value;
  return { value, kind: percent ? "percent" : symbol ? "currency" : "plain", symbol, symbolAfter, decimals, thousands };
}

/** Excel number format for a group of parsed numbers that share a kind. */
function formatFor(kind: ParsedNumber["kind"], symbol: string | undefined, decimals: number, thousands: boolean, symbolAfter = false): string {
  const dec = decimals > 0 ? "." + "0".repeat(decimals) : "";
  if (kind === "percent") return `0${dec}%`;
  const base = `${thousands || kind === "currency" ? "#,##0" : "0"}${dec}`;
  return kind === "currency" && symbol ? (symbolAfter ? `${base}" ${symbol}"` : `"${symbol}"${base}`) : base;
}

// ---------------------------------------------------------------------------------------------
// dates
// ---------------------------------------------------------------------------------------------

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const monthIndex = (w: string) => MONTHS.indexOf(w.slice(0, 3).toLowerCase());

export function excelSerial(y: number, m: number, d: number): number | null {
  if (m < 1 || m > 12 || d < 1) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return Math.round(dt.getTime() / 86400000) + 25569;
}

const fullYear = (y: string) => {
  if (y.length === 4) return Number(y);
  const n = Number(y);
  return n <= 49 ? 2000 + n : 1900 + n;
};

export interface ParsedDate {
  /** Possible readings: one when the text settles it, two for a slash date that could go either way. */
  options: { serial: number; order: "dmy" | "mdy" | "fixed" }[];
  format: string;
  /** Set for 09/02/2026-style dates: the number format is chosen per column once the order is known. */
  numeric?: { sep: string; twoDigitYear: boolean };
}

/** Reads one cell as a date, or null. */
export function parseDate(text: string): ParsedDate | null {
  const s = text.replace(NBSP, " ").trim();
  if (!s || s.length > 30) return null;
  let m: RegExpExecArray | null;
  // 2026-02-09
  if ((m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(s))) {
    const serial = excelSerial(Number(m[1]), Number(m[2]), Number(m[3]));
    return serial === null ? null : { options: [{ serial, order: "fixed" }], format: "yyyy-mm-dd" };
  }
  // 09/02/2026, 9.2.26, 09-02-2026
  if ((m = /^(\d{1,2})([/.-])(\d{1,2})\2(\d{4}|\d{2})$/.exec(s))) {
    const a = Number(m[1]), b = Number(m[3]), y = fullYear(m[4]);
    const sep = m[2];
    const options: ParsedDate["options"] = [];
    const d1 = excelSerial(y, b, a); // day first: a = day, b = month
    const d2 = excelSerial(y, a, b); // month first
    if (d1 !== null && d1 === d2) options.push({ serial: d1, order: "fixed" }); // 05/05/2026: same either way
    else {
      if (d1 !== null) options.push({ serial: d1, order: "dmy" });
      if (d2 !== null) options.push({ serial: d2, order: "mdy" });
    }
    if (!options.length) return null;
    return { options, format: "", numeric: { sep, twoDigitYear: m[4].length === 2 } };
  }
  // 9 Feb 2026, 09-Feb-2026, 9 February 2026
  if ((m = /^(\d{1,2})[ -]([A-Za-z]{3,9})\.?,?[ -](\d{4}|\d{2})$/.exec(s))) {
    const mi = monthIndex(m[2]);
    if (mi < 0) return null;
    const serial = excelSerial(fullYear(m[3]), mi + 1, Number(m[1]));
    return serial === null ? null : { options: [{ serial, order: "fixed" }], format: "d mmm yyyy" };
  }
  // Feb 9, 2026 / February 9 2026
  if ((m = /^([A-Za-z]{3,9})\.? (\d{1,2}),? (\d{4})$/.exec(s))) {
    const mi = monthIndex(m[1]);
    if (mi < 0) return null;
    const serial = excelSerial(Number(m[3]), mi + 1, Number(m[2]));
    return serial === null ? null : { options: [{ serial, order: "fixed" }], format: "d mmm yyyy" };
  }
  return null;
}

/** Number format for a slash/dash/dot date once the order is known. */
function numericDateFormat(sep: string, order: "dmy" | "mdy", twoDigitYear: boolean): string {
  const y = twoDigitYear ? "yy" : "yyyy";
  return order === "dmy" ? `dd${sep}mm${sep}${y}` : `mm${sep}dd${sep}${y}`;
}

// ---------------------------------------------------------------------------------------------
// columns
// ---------------------------------------------------------------------------------------------

/** Headings that mean the numbers are labels, not quantities. */
const IDENTIFIER_HEADING = /\b(id|ids|no\.?|num|number|code|zip|zipcode|postcode|postal|phone|tel|mobile|account|acct|ref|reference|sku|isbn|iban|ssn|pin|invoice|order|serial|barcode)\b|#/i;

const COLUMN_SHARE = 0.8;

export interface ConvertResult {
  cells: SheetCell[][];
  /** Things worth telling the person, e.g. dates left as text because day/month is unclear. */
  notes: string[];
  numericCells: number;
  dateCells: number;
}

/**
 * Converts the body of a table. `headerRows` rows at the top are kept as text. Rows may have
 * different lengths; short rows are padded with empty text.
 */
export function convertTable(rows: string[][], headerRows: number, dateOrder: DateOrder = "auto"): ConvertResult {
  const width = Math.max(0, ...rows.map((r) => r.length));
  const cells: SheetCell[][] = rows.map((r) => Array.from({ length: width }, (_, c) => ({ t: "s", v: r[c] ?? "" }) as SheetCell));
  const notes: string[] = [];
  let numericCells = 0;
  let dateCells = 0;

  for (let c = 0; c < width; c++) {
    const heading = rows.slice(0, headerRows).map((r) => r[c] ?? "").join(" ");
    const idLike = IDENTIFIER_HEADING.test(heading);
    const body: { row: number; text: string }[] = [];
    for (let r = headerRows; r < rows.length; r++) {
      const text = (rows[r][c] ?? "").trim();
      if (text) body.push({ row: r, text });
    }
    if (body.length === 0) continue;

    // Dates first: a column of dates must not be mistaken for numbers.
    const dates = body.map((b) => ({ ...b, parsed: parseDate(b.text) }));
    const dateHits = dates.filter((d) => d.parsed).length;
    if (dateHits / body.length >= COLUMN_SHARE && dateHits >= 1) {
      // Settle day/month order for slash dates from the whole column.
      let order: "dmy" | "mdy" | null = null;
      const evidence = new Set<"dmy" | "mdy">();
      for (const d of dates) {
        if (!d.parsed) continue;
        const kinds = d.parsed.options.map((o) => o.order).filter((o) => o !== "fixed") as ("dmy" | "mdy")[];
        if (kinds.length === 1) evidence.add(kinds[0]);
      }
      if (evidence.size === 1) {
        order = Array.from(evidence)[0];
        if (dateOrder !== "auto" && dateOrder !== order) {
          const example = dates.find((d) => d.parsed && d.parsed.options.filter((o) => o.order !== "fixed").length === 1 && d.parsed.options.length === 1 && d.parsed.numeric)?.text;
          notes.push(`These dates can only be ${order === "mdy" ? "month-first" : "day-first"}${example ? ` (for example ${example})` : ""}, so that order was used.`);
        }
      }
      else if (evidence.size === 0 && dateOrder !== "auto") order = dateOrder;
      const ambiguous = dates.some((d) => d.parsed && d.parsed.options.length > 1 && d.parsed.options.every((o) => o.order !== "fixed"));
      if (evidence.size === 2) {
        notes.push(`Column ${c + 1} mixes day-first and month-first dates, so its dates were left as text.`);
        continue;
      }
      if (ambiguous && !order) {
        notes.push(`Dates like ${dates.find((d) => d.parsed && d.parsed.options.length > 1)!.text} could be day-first or month-first, so they were left as text. Choose the date order above and convert again to get real dates.`);
        continue;
      }
      let usable = 0;
      const pending: { row: number; serial: number; fmt: string }[] = [];
      for (const d of dates) {
        if (!d.parsed) continue;
        const pick = d.parsed.options.find((o) => o.order === "fixed") ?? (order ? d.parsed.options.find((o) => o.order === order) : undefined);
        if (!pick) continue;
        let fmt = d.parsed.format;
        if (d.parsed.numeric) {
          // One order for the whole column: a date like 09/09/2026 reads the same either way and takes the column's order.
          const colOrder = pick.order !== "fixed" ? pick.order : (order ?? (dateOrder !== "auto" ? dateOrder : "mdy"));
          fmt = numericDateFormat(d.parsed.numeric.sep, colOrder, d.parsed.numeric.twoDigitYear);
        }
        pending.push({ row: d.row, serial: pick.serial, fmt });
        usable++;
      }
      if (usable === dateHits) {
        for (const p of pending) cells[p.row][c] = { t: "d", v: p.serial, z: p.fmt };
        dateCells += usable;
        continue;
      }
    }

    if (idLike) continue;
    const nums = body.map((b) => ({ ...b, parsed: parseNumber(b.text) }));
    const hits = nums.filter((n) => n.parsed);
    if (hits.length === 0) continue;
    // Mostly numbers, or numbers with plain labels in the same column (Subtotal, Total due ...): the amounts
    // still become numbers and the labels stay text.
    const labelsOnly = nums.filter((n) => !n.parsed).every((n) => /[A-Za-z\u00C0-\u024F\u0370-\u03FF\u0400-\u04FF]{3,}/.test(n.text));
    const share = hits.length / body.length;
    if (share < COLUMN_SHARE && !(labelsOnly && share >= 0.5 && hits.length >= 2)) continue;
    // One number format per kind in the column, so the column looks consistent.
    const groups = new Map<string, ParsedNumber[]>();
    for (const h of hits) (groups.get(h.parsed!.kind) ?? groups.set(h.parsed!.kind, []).get(h.parsed!.kind)!).push(h.parsed!);
    const formats = new Map<string, string>();
    for (const [kind, list] of Array.from(groups)) {
      const decimals = Math.max(...list.map((p) => p.decimals));
      const thousands = list.some((p) => p.thousands);
      const symbols = list.map((p) => p.symbol).filter(Boolean) as string[];
      const symbol = symbols.length >= list.length / 2 ? symbols.sort((a, b) => symbols.filter((x) => x === b).length - symbols.filter((x) => x === a).length)[0] : undefined;
      const after = list.filter((p) => p.symbol === symbol && p.symbolAfter).length > list.filter((p) => p.symbol === symbol).length / 2;
      formats.set(kind, formatFor(kind as ParsedNumber["kind"], symbol, decimals, thousands, after));
    }
    for (const h of hits) {
      cells[h.row][c] = { t: "n", v: h.parsed!.value, z: formats.get(h.parsed!.kind)! };
      numericCells++;
    }
  }
  return { cells, notes, numericCells, dateCells };
}

/**
 * What a number format says about the cell itself: "%" for a percentage, the quoted currency symbol
 * for a currency amount, "" for a plain number. Formats are only merged between cells of the same
 * kind, so one "$3,000" cell never turns the plain numbers in its column into dollars.
 */
export function numberFormatKind(z: string | undefined): string {
  if (!z) return "";
  if (/%$/.test(z)) return "%";
  return /^"[^"]*"/.exec(z)?.[0] ?? /"[^"]*"$/.exec(z)?.[0] ?? "";
}

/** One number format out of several found for the same column (on different sheets): thousands separators and the most decimals win. */
export function mergeNumberFormats(formats: string[]): string {
  const uniq = Array.from(new Set(formats));
  if (uniq.length <= 1) return uniq[0] ?? "General";
  const thousands = uniq.some((f) => f.includes("#,##0"));
  const decimals = Math.max(...uniq.map((f) => (/\.(0+)/.exec(f.replace(/"[^"]*"/g, ""))?.[1].length ?? 0)));
  const percent = uniq.some((f) => /%$/.test(f));
  const base = `${thousands ? "#,##0" : "0"}${decimals ? "." + "0".repeat(decimals) : ""}`;
  if (percent) return `${base}%`;
  const withSymbol = uniq.find((f) => f.includes('"'));
  if (!withSymbol) return base;
  const lead = /^"[^"]*"/.exec(withSymbol)?.[0];
  if (lead) return `${lead}${base}`;
  const tail = /"[^"]*"$/.exec(withSymbol)?.[0];
  return tail ? `${base}${tail}` : base;
}
